import React, { forwardRef, useId } from 'react';
import { cx } from './cx';

export interface UiInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  prefixNode?: React.ReactNode;
  suffixNode?: React.ReactNode;
}

/** Cosmos text field (1d): glass surface, ice border, cyan focus ring. */
const Input = forwardRef<HTMLInputElement, UiInputProps>(function Input(
  { label, hint, error, prefixNode, suffixNode, className, id, ...rest },
  ref
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <div className={cx('flex flex-col gap-1.5', className)}>
      {label && (
        <label
          htmlFor={inputId}
          className="font-ui text-[length:var(--text-label)] font-medium uppercase tracking-[0.22em] text-caption-muted"
        >
          {label}
        </label>
      )}
      <div
        className={cx(
          'flex items-center gap-2 rounded-control border bg-white/5 px-3.5',
          'transition-all duration-150 focus-within:shadow-focus-ring',
          error
            ? 'border-danger/70 focus-within:border-danger'
            : 'border-border-ice focus-within:border-info/70'
        )}
      >
        {prefixNode && <span className="text-caption-muted shrink-0">{prefixNode}</span>}
        <input
          ref={ref}
          id={inputId}
          className="h-11 w-full bg-transparent font-ui text-[length:var(--text-body)] text-ink placeholder:text-caption-muted focus:outline-none"
          {...rest}
        />
        {suffixNode && <span className="text-caption-muted shrink-0">{suffixNode}</span>}
      </div>
      {(error || hint) && (
        <p
          className={cx(
            'font-ui text-[length:var(--text-caption)]',
            error ? 'text-danger-soft' : 'text-caption-muted'
          )}
        >
          {error ?? hint}
        </p>
      )}
    </div>
  );
});

export default Input;
